import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { workspaceUrlFor } from '../utils/workspace-urls';

/**
 * Keeps signed-in clients inside the customer workspace.
 *
 * Guests browse the public catalog freely. A signed-in customer has a sidebar
 * and the rest of the workspace, so the public shell would strand them outside
 * it — they are forwarded to the workspace page that shows the same thing
 * instead (see workspaceUrlFor). Other provider roles are untouched: they keep
 * today's public view, which is the read-only one their accounts already get.
 *
 * Deliberately token-based rather than isAuthenticated(): this runs on public
 * URLs, where an expired token must not trigger a logout-and-redirect. Under
 * prerendering getToken() reads the in-memory shim and returns null, so the
 * crawled HTML is always the guest view.
 */
export const customerWorkspaceRedirect: CanActivateChildFn = (_childRoute, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.getToken() || auth.isTokenExpiredSafe() || auth.getUserRole() !== 'customer') {
    return true;
  }

  const target = workspaceUrlFor(state.url);
  return target ? router.parseUrl(target) : true;
};
